/* cart-shipping-estimator.js
 *
 * Posts the cart quantity + postcode to the rates API and shows the amount plus
 * the packaging breakdown. Same approach as the product page volume calculator:
 * the server decides the packing, this just renders it.
 *
 * There is NO flat-rate path and no per-unit multiplication. Every quantity
 * goes to the API. If the API is unreachable the customer sees an error rather
 * than a made-up number.
 *
 * The API answers in Shopify carrier-service shape:
 *   { "rates": [{ "service_name": "Australia Post",
 *                 "description":  "1 x 10-pack + 1 x 1-pack",
 *                 "total_price":  "9640" }] }        <- cents, string
 *
 * Expected markup:
 *   <cart-shipping-estimator data-api="..." data-cart-quantity="12">
 *     <input data-shipping-postcode>
 *     <p data-shipping-status></p>
 *   </cart-shipping-estimator>
 *
 * Optional hooks elsewhere in the cart totals:
 *   [data-shipping-line]   wrapper, un-hidden once a quote lands
 *   [data-shipping-amount] receives the formatted amount
 */

(function () {
  if (window.customElements.get('cart-shipping-estimator')) return;

  const DEBOUNCE_MS = 0;
  const UNIT_GRAMS = 1000;

  // Shared with the postcode sync script in cart-summary.liquid. Must match, or
  // a postcode entered on the product page will not carry through to the cart.
  const STORAGE_KEY = 'shipping_postcode';

  // Friendly names for the pack sizes the API reports. Keep in step with the
  // rates server. Sizes not listed here fall back to "N-pack".
  const PACK_NAMES = {
    1: 'Medium Pre‑Paid Satchel',
    2: 'Extra large pre-paid satchel',
    3: 'Extra large pre-paid satchel',
    4: 'Extra‑Large Pre‑Paid Box',
    10: '10-pack shipper',
  };

  const money = (dollars) =>
    '$' +
    dollars.toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  // "box" -> "boxes", "satchel" -> "satchels"
  const plural = (word) => word + (/(x|s|ch|sh)$/i.test(word) ? 'es' : 's');

  /**
   * "1 x 10-pack + 2 x 3-pack" -> "1 shipper carton + 2 extra large satchels"
   * Falls back to the raw string if nothing parses.
   */
  function describePacks(description) {
    if (!description) return '';

    const parts = [];
    const pattern = /(\d+)\s*[x\u00D7]\s*(\d+)\s*-\s*pack/gi;
    let match;

    while ((match = pattern.exec(description)) !== null) {
      const count = parseInt(match[1], 10);
      const units = parseInt(match[2], 10);
      const name = PACK_NAMES[units] || units + '-pack';
      parts.push(count + ' ' + (count > 1 ? plural(name) : name));
    }

    if (parts.length === 0) return description;
    return parts.join(' + ');
  }

  class CartShippingEstimator extends HTMLElement {
    connectedCallback() {
      this.api = this.dataset.api;
      this.input = this.querySelector('[data-shipping-postcode]');
      this.status = this.querySelector('[data-shipping-status]');

      if (!this.api || !this.input || !this.status) {
        console.warn('[CartShipping] missing api, input or status element');
        return;
      }

      this.cache = new Map();
      this.requestId = 0;
      this.debounce = null;
      this.lastQuantity = this.quantity;

      // Accept synthetic events too — the postcode sync fires `input`
      // programmatically to prefill this from the product page value.
      this.input.addEventListener('input', () => {
        clearTimeout(this.debounce);
        const value = this.input.value.trim();

        if (!/^\d{4}$/.test(value)) {
          this.clear();
          return;
        }

        this.save(value);
        this.debounce = setTimeout(() => this.estimate(), DEBOUNCE_MS);
      });

      // The cart line quantity changes -> data-cart-quantity is rewritten in
      // place by the morph. Without this the amount would sit there stale,
      // still quoting for the previous quantity.
      this.observer = new MutationObserver(() => {
        if (this.quantity === this.lastQuantity) return;
        this.lastQuantity = this.quantity;
        if (/^\d{4}$/.test(this.input.value.trim())) this.estimate();
        else this.clear();
      });
      this.observer.observe(this, {
        attributes: true,
        attributeFilter: ['data-cart-quantity'],
      });

      // Restore a postcode from a previous page, then quote straight away.
      if (!this.input.value) {
        const saved = this.load();
        if (saved) this.input.value = saved;
      }
      if (/^\d{4}$/.test(this.input.value.trim())) this.estimate();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      clearTimeout(this.debounce);
    }

    get quantity() {
      return parseInt(this.dataset.cartQuantity, 10) || 0;
    }

    load() {
      try {
        const value = localStorage.getItem(STORAGE_KEY) || '';
        return /^\d{4}$/.test(value) ? value : '';
      } catch (e) {
        return '';
      }
    }

    save(value) {
      try {
        localStorage.setItem(STORAGE_KEY, value);
      } catch (e) {}
    }

    setStatus(text, state) {
      this.status.textContent = text;
      this.status.className =
        'cart-shipping-estimator__result' + (state ? ' is-' + state : '');
    }

    clear() {
      this.setStatus('', '');
      this.updateTotals(null);
    }

    /** Reveal and fill the Shipping line in the cart totals. */
    updateTotals(dollars) {
      const line = document.querySelector('[data-shipping-line]');
      const amount = document.querySelector('[data-shipping-amount]');
      if (!line || !amount) return;

      if (dollars === null) {
        line.classList.add('hidden');
        amount.textContent = '';
        return;
      }

      amount.textContent = money(dollars);
      line.classList.remove('hidden');
    }

    async estimate() {
      const postcode = this.input.value.trim();
      const quantity = this.quantity;

      if (!/^\d{4}$/.test(postcode) || quantity < 1) {
        this.clear();
        return;
      }

      const cacheKey = postcode + ':' + quantity;
      if (this.cache.has(cacheKey)) {
        this.render(this.cache.get(cacheKey));
        return;
      }

      const requestId = ++this.requestId;
      this.setStatus('Calculating…', 'loading');

      try {
        const response = await fetch(this.api, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            rate: {
              destination: { postal_code: postcode, country: 'AU' },
              items: [{ quantity: quantity, grams: UNIT_GRAMS }],
            },
          }),
        });

        // Quantity or postcode changed while this was in flight
        if (requestId !== this.requestId) return;

        if (!response.ok) throw new Error('Server error ' + response.status);

        const data = await response.json();
        const rate = (data.rates || [])[0];
        if (!rate) throw new Error('No rates returned');

        this.cache.set(cacheKey, rate);
        this.render(rate);
      } catch (error) {
        if (requestId !== this.requestId) return;
        console.error('[CartShipping]', error);
        this.setStatus(
          'Shipping quote unavailable. Please try again at checkout.',
          'error'
        );
        this.updateTotals(null);
      }
    }

    render(rate) {
      const dollars = parseFloat(rate.total_price) / 100;
      if (isNaN(dollars)) {
        this.setStatus(
          'Shipping quote unavailable. Please try again at checkout.',
          'error'
        );
        this.updateTotals(null);
        return;
      }

      const packs = describePacks(rate.description);
      this.setStatus(
        money(dollars) + (packs ? ' — ships as ' + packs : ''),
        'success'
      );
      this.updateTotals(dollars);
    }
  }

  window.customElements.define('cart-shipping-estimator', CartShippingEstimator);
})();
